import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages } from 'react-intl';
import { Link } from 'react-router-dom';

import LINKS from 'constants/links';
import { venuePropTypes } from 'constants/propTypes';
import Button from 'components/Button';
import { Card, CardTitle } from 'components/Card';
import Message from 'components/Message';
import { Title } from 'features/App';

import AccountInformationSection from './AccountInformationSection';
import { ChangeLanguageSection } from './ChangeLanguageSection';
import PasswordFormSection from './PasswordFormSection';
import { VenuePagesSection } from './VenuePagesSection';
import { MFASection } from './MFASection';

const messages = defineMessages({
  title: {
    id: 'settings.title',
    defaultMessage: 'Settings',
  },
  frontDeskTitle: {
    id: 'settings.front-desk-title',
    defaultMessage: 'Pike13 Integration',
  },
  frontDeskButtonText: {
    id: 'settings.front-desk-button-text',
    defaultMessage: 'Start Integration',
  },
  termsAndConditionsTitle: {
    id: 'settings.terms-and-conditions-title',
    defaultMessage: 'Standard Terms & Conditions',
  },
  termsAndConditionsButtonText: {
    id: 'settings.terms-and-conditions-button-text',
    defaultMessage: 'View Standard Terms',
  },
  trialerAndCampaignTermsTitle: {
    id: 'settings.trialer-and-campaigns-terms-title',
    defaultMessage: 'Trialer Program Terms and Campaign Terms',
  },
  trialerAndCampaignTermsButtonText: {
    id: 'settings.trialer-and-campaigns-terms-button-text',
    defaultMessage: 'View Terms',
  },
});

const Settings = ({
  brazeEmailIsBounced,
  frontdeskUrl,
  handleChangeLocale,
  handlePasswordUpdate,
  isLoaded,
  languages,
  locale,
  showFrontdeskLink,
  standardTermsUrl,
  user,
  venues,
  venueId,
}) =>
  !isLoaded ? (
    <Card loading />
  ) : (
    <>
      <Message as={Title} message={messages.title} />

      <AccountInformationSection
        user={user}
        brazeEmailIsBounced={brazeEmailIsBounced}
        venueId={venueId}
        locale={locale}
      />

      <VenuePagesSection venues={venues} />

      <PasswordFormSection onSubmit={handlePasswordUpdate} />

      <MFASection />

      <ChangeLanguageSection languages={languages} currentLocale={locale} handleChangeLocale={handleChangeLocale} />

      {showFrontdeskLink && (
        <Card collapse>
          <div className="settings__button-card">
            <Message as={CardTitle} message={messages.frontDeskTitle} />
            <Link to={frontdeskUrl}>
              <Message as={Button} type={Button.TYPES.BUTTON} message={messages.frontDeskButtonText} />
            </Link>
          </div>
        </Card>
      )}

      <Card collapse>
        <div className="settings__button-card">
          <Message as={CardTitle} message={messages.termsAndConditionsTitle} />
          <Link to={standardTermsUrl} target="_blank" rel="noopener noreferrer">
            <Message as={Button} type={Button.TYPES.BUTTON} message={messages.termsAndConditionsButtonText} />
          </Link>
        </div>
      </Card>
      <Card collapse>
        <div className="settings__button-card">
          <Message as={CardTitle} message={messages.trialerAndCampaignTermsTitle} />
          <a href={LINKS.trialerProgramTermsAgreement.href} target="_blank" rel="noopener noreferrer">
            <Message as={Button} type={Button.TYPES.BUTTON} message={messages.trialerAndCampaignTermsButtonText} />
          </a>
        </div>
      </Card>
    </>
  );

Settings.propTypes = {
  brazeEmailIsBounced: PropTypes.bool,
  frontdeskUrl: PropTypes.string,
  handleChangeLocale: PropTypes.func,
  handlePasswordUpdate: PropTypes.func,
  isLoaded: PropTypes.bool,
  languages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
    })
  ),
  locale: PropTypes.string,
  showFrontdeskLink: PropTypes.bool,
  standardTermsUrl: PropTypes.string,
  user: PropTypes.shape({
    email: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
  }),
  venues: PropTypes.arrayOf(PropTypes.shape(venuePropTypes)),
  venueId: PropTypes.number,
};

export default Settings;
